import React, {
  useState,
  useMemo,
  useRef,
  createContext,
  useEffect,
  useContext,
  useCallback
} from 'react'
import { PostJobHeader } from './PostJobHeader'
import { AdvertiserPrimaryButton } from './AdvertiserPrimaryButton'
import { PostJobSteps } from './PostJobSteps'
import dynamic from 'next/dynamic'
import {
  validateSteps,
  getEventName,
  getFormattedBody,
  advertiserAnalyticOptions
} from '../utils/functions'
import { draftPost } from '../services/postJob'
import { postMethod, patchMethod } from '../utils/api'
import { convertToFormData } from '../utils/formData'
import { trackEvent } from '../analytics'
import { Media } from './Media'
import { AdvertiserHeader } from './AdvertiserHeader'
import classNames from 'classnames'
import { CircularSpinner } from '../components/CircularSpinner'
import { NextLink } from './NextLink'
import { ADVERTISER_SUBMIT_JOB } from '../constants/all'
import {
  ENTERPRISE_SUBMIT_KEYS,
  GET_POST_KEYS,
  SUBMIT_KEYS,
  ANY_GENDER_ID
} from '../constants/advertiser'
import { useRouterHook } from '../hooks/useRouterHook'
import useTranslation from '../hooks/useTranslation'
import { getStep4PhoneNumberFromLocale } from '../utils/functions'
import PaymentComponent from '@/commonComponents/PaymentComponent'
import { FooterModal } from '@/components/FooterModal'
import EnterpriseErrorComponent from '@/Enterprise/commonComponents/EnterpriseErrorComponent'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import SignalWifiConnectedNoInternet4Icon from '@mui/icons-material/SignalWifiConnectedNoInternet4'
import { AdvertiserContext } from '@/context/AdvertiserContext'
import { SharedContext } from '@/context/SharedContext'
import { EDUCATION_KEYS } from '../constants/hire/advertiserForm'
import {
  getAdvertiserContact,
  getLegacyBasicDetails,
  isAndroid,
  isLegacyNewsWebView,
  isModernWebView
} from '@/android/bridge'

export const PostJobContext = createContext()
const Step1 = dynamic(() => import('./PostJobStepsComponents').then((mod) => mod.Step1), {
  ssr: false
})
const Step2 = dynamic(() => import('./PostJobStepsComponents').then((mod) => mod.Step2), {
  ssr: false
})
const Step3 = dynamic(() => import('./PostJobStepsComponents').then((mod) => mod.Step3), {
  ssr: false
})

export const PostJobContainer = ({ data, loader, isAndroidWebview, submissionId }) => {
  const [isPostJobMounted, setPostJobMounted] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)

  const statusCodes = [200, 201, 202, 203, 204, 205, 206, 207, 208, 226]

  const conditions = JSON?.parse(process.env.NEXT_PUBLIC_COMPONENT_SHOW)
  const childRef = useRef()
  const { replaceThePageInAdvertiser } = useRouterHook()
  const translation = useTranslation()

  const { location, loginDetails, utmParams, windowSize, language, isOnline } =
    useContext(SharedContext)

  const {
    formData,
    setFormData,
    setFormContext,
    tempFormData,
    setTempFormData,
    postJobPresentStep,
    setpostJobPresentStep,
    totalAmount,
    setTotalAmount,
    setIsDistrictSelected,
    editJobSubmissionId,
    enterpriseLoginDetails,
    languageFromAndroid,
    setQueryParams
  } = useContext(AdvertiserContext)

  const [errorId, setErrorId] = useState([])
  const [formInitiated, setFormInitiated] = useState(false)
  const [btnLoader, setbtnLoader] = useState(false)
  const [btnDisable, setbtnDisable] = useState(false)
  const [locale, setLocale] = useState(null)
  const [submissionError, setSubmissionError] = useState({})

  const NUMBER_OF_STEPS = useRef(4)
  const context = useMemo(() => {
    return data?.context
  }, [data?.context, language])

  const draft = useMemo(() => data?.draft_data, [data?.draft_data])

  const onChange = useCallback(
    (id, value) => {
      if (value != undefined && value != null && String(value)?.length > 0)
        setErrorId((prv) => [...prv?.filter((errorId) => errorId != id)])
      setFormData((prv) => {
        if (id == 'additionalSkills') {
          value = value?.split(',')
          if (prv[id] && prv[id].length > 0) {
            let temp = prv[id]
            temp.push(...value)
            prv[id] = temp
          } else {
            prv[id] = value
          }
        } else {
          prv[id] = value
        }
        if (id == 'company_email' && localStorage.getItem('submissionError')) {
          localStorage.removeItem('submissionError')
        }
        if (id == 'job_category') prv['primary_job_role'] = ''
        return { ...prv }
      })
    },
    [utmParams, loginDetails, errorId]
  )

  const getMobileNumber = (loginDetails = {}) => {
    if (!isAndroid()) {
      // 1. Web fallback
      return loginDetails?.mobileNumber || ''
    }

    if (isLegacyNewsWebView()) {
      return getLegacyBasicDetails()?.mobile_no?.toString() || ''
    }

    if (isModernWebView()) {
      // 3. Modern SDK WebViews (Jobs / News)
      return getAdvertiserContact() || ''
    }

    // 4. Fallback
    return ''
  }

  useEffect(() => {
    if (!formInitiated) {
      trackEvent({
        name: 'form_initiation',
        searchParams: utmParams,
        options: advertiserAnalyticOptions({
          'Mobile number': loginDetails?.mobileNumber,
          'WhatsApp Opt_in': loginDetails?.whatsAppOptIn,
          enterprise: !!enterpriseLoginDetails?.advertiserId
        })
      })
      setFormInitiated(true)
    }
  }, [])

  useEffect(() => {
    setFormContext(data?.context || {})
  }, [data?.context])

  // for building the formData initially
  useEffect(() => {
    let temp = {}

    // incase of news app, for not getting data from draft need to add a condition
    if (draft && !isLegacyNewsWebView()) {
      Object?.keys(draft).forEach((element) => {
        if (!formData?.[element] || formData?.element == 0) temp[element] = draft?.[element]
      })
    }

    // for platform do they need any other value for jobs app
    temp['platform'] = !isAndroid() ? 'web' : 'android'
    if (formData?.is_whatsapp_number === undefined) temp['is_whatsapp_number'] = true

    // for mobile_no also this case will only be there for lokal news app
    if (!temp['mobile_no'] && !formData?.['mobile_no']) temp['mobile_no'] = getMobileNumber()

    if (!temp['preferred_gender'] && !formData?.['preferred_gender']) {
      temp['preferred_gender'] = ANY_GENDER_ID
    }

    if (!enterpriseLoginDetails?.advertiserId)
      temp['preferred_location'] = Number(process.env.NEXT_PUBLIC_LOCATION_PREFERENCE_DISTRICT)

    if (temp?.['course_requirement']) {
      temp[EDUCATION_KEYS.COURSE] = temp?.['course_requirement']?.[0]?.['course']?.['id']
      temp[EDUCATION_KEYS.COURSE_TITLE] = temp?.['course_requirement']?.[0]?.['course']?.['title']
      temp[EDUCATION_KEYS.CERTIFICATE] = temp?.['course_requirement']?.[0]?.['certificate']?.['id']
      temp[EDUCATION_KEYS.CERTIFICATE_TITLE] =
        temp?.['course_requirement']?.[0]?.['certificate']?.['title']
    }

    if (context?.['advertiser_info_exists']) {
      temp['advertiser_company_name'] = context['advertiser_info']['company_name']
      temp['company_name'] = context['advertiser_info']['company_name']
      temp['company_size'] = context['advertiser_info']['company_size']
      temp['company_type'] = context['advertiser_info']['company_type']
      temp['is_consultancy_name'] = context['advertiser_info']['is_consultancy_name']
    } else {
      temp['is_consultancy_name'] = null
    }

    if (isLegacyNewsWebView()) {
      // for news app webview from here we fill form data
      if (submissionId) {
        if (data?.draft_data?.salary_text) {
          const formDataFromRaw = convertToFormData(data?.draft_data, null)
          setFormData((prv) => ({
            ...prv,
            ...formDataFromRaw,
            ...JSON?.parse(JSON?.stringify(temp))
          }))
        }
      } else {
        setFormData({
          ...JSON?.parse(JSON?.stringify(temp)),
          primary_job_role: '',
          shift_timing: ''
        })
      }
    } else if (submissionId || editJobSubmissionId) {
      //for pure edit flow and payments from here we fill the formData
      if (data?.draft_data?.salary_text) {
        const formDataFromRaw = convertToFormData(data?.draft_data, null)
        setFormData((prv) => ({
          ...prv,
          ...JSON?.parse(JSON?.stringify(temp)),
          ...formDataFromRaw
        }))
      }
    } else {
      setFormData((prv) => ({ ...JSON?.parse(JSON?.stringify(temp)), ...prv }))
    }
    setPostJobMounted(true)
  }, [draft, loginDetails])

  // For news app android job form initial step rendering whether fourth or first step
  useEffect(() => {
    if (isLegacyNewsWebView()) {
      if (submissionId) {
        setpostJobPresentStep(4)
      } else {
        setpostJobPresentStep(1)
      }
    }
  }, [])

  useEffect(() => {
    window?.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }, [postJobPresentStep])

  useEffect(() => {
    // for news app back gesture handler
    if (isLegacyNewsWebView()) {
      Android.onPageSuccessfullyLoaded()
    }
  }, [postJobPresentStep])

  // for locale setting for the mobile number to display
  useEffect(() => {
    if (formData?.['posting_locations']?.length && postJobPresentStep == 4) {
      if (!isAndroid()) {
        const stateId = data.context['job_district_list']?.find(
          (item) => item.id == formData?.['posting_locations'][0]
        )?.state__id
        const locale = data.context['job_state_list']?.find((item) => item.id == stateId)?.locale
        setLocale(locale)
      }
    }
  }, [postJobPresentStep])

  const scrollToElement = useCallback((id) => {
    let element = document?.getElementById?.(id)

    element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  const listToCommaSeparator = (list) => (Array?.isArray(list) ? list : [])?.join(',')

  const findErrorIds = (button) => {
    let ids = []
    let step
    if (button == 'review') {
      for (let index = 1; index <= NUMBER_OF_STEPS?.current; index++) {
        const step_ids = validateSteps({
          presentStep: index,
          formData,
          enterpriseAdvertiserId: enterpriseLoginDetails?.advertiserId
        })
        if (step_ids?.length > 0 && !step) step = index
        ids = [...ids, ...step_ids]
      }
    } else
      ids = validateSteps({
        presentStep: postJobPresentStep,
        formData,
        enterpriseAdvertiserId: enterpriseLoginDetails?.advertiserId
      })
    return { ids, step }
  }

  const findSubmitData = useCallback(
    (index) => {
      let temp = {}
      Object?.keys(GET_POST_KEYS[index])?.map((item) => {
        temp[GET_POST_KEYS[index][item]] = formData?.[item] || ''
      })
      if (index === 1) {
        temp['job_fee_reason'] = listToCommaSeparator(temp?.['job_fee_reason'])
      } else {
        temp['contact_preference'] = formData?.['contact_preference']
        temp['platform'] = 1
        temp['fee_reason'] = listToCommaSeparator(temp?.['fee_reason'])
      }
      if (!temp?.['title']) temp['title'] = formData?.['title']
      temp['assets'] = temp?.['assets']?.length ? JSON.stringify(temp['assets']) : temp['assets']
      temp['preferred_languages'] = temp?.['preferred_languages']?.length
        ? JSON.stringify(temp['preferred_languages'])
        : temp['preferred_languages']
      temp['posting_locations'] = listToCommaSeparator(temp?.['posting_locations'])
      return temp
    },
    [formData]
  )

  const handleSubmit = useCallback(async () => {
    const isTempFormDataPresent = tempFormData?.isPresent
    const hasSubmissionId = !!formData?.submissionId || !!submissionId
    const result = findSubmitData(isTempFormDataPresent || hasSubmissionId ? 2 : 1)
    const time = new Date()

    /* ---------- logo handling (unchanged) ---------- */
    if (!isTempFormDataPresent && formData['log_url']) {
      result['job_company_logo'] = await createFile(formData['log_url'], `company_logo_${time}`)
    } else if (isTempFormDataPresent) {
      if (formData['isLogoPresent'] && !formData['log_url']) {
        result['job_company_logo'] = 'delete'
      } else if (formData['log_url']?.includes('blob')) {
        result['job_company_logo'] = await createFile(formData['log_url'], `company_logo_${time}`)
      }
    }

    /* ---------- enterprise decorations ---------- */
    if (enterpriseLoginDetails?.advertiserId) {
      result['advertiser_id'] = enterpriseLoginDetails.advertiserId
      result['submission_type'] = 'enterprise'
    }

    /* ---------- legacy fall-backs ---------- */
    if (!result['job_location_city'] && !result['job_city']) {
      result['job_district'] = formData['job_district']
    }
    if (!result['additional_skills']) result['additional_skills'] = formData['additionalSkills']
    if (!result['skills']) result['skills'] = formData['skills']
    if (!result['company_email']) result['company_email'] = formData['company_email']

    if (isTempFormDataPresent) result['platform'] = 'web'
    result['source'] = 'websubmission'

    /* ---------- 2.  Network call ---------- */
    const requestConfig = {
      data: getFormattedBody({
        data: result,
        step: isTempFormDataPresent || hasSubmissionId ? 2 : 1,
        keys: enterpriseLoginDetails?.advertiserId ? ENTERPRISE_SUBMIT_KEYS : SUBMIT_KEYS
      }),
      withStatusCode: true,
      isAndroidWebview: isAndroid()
    }

    const res =
      isTempFormDataPresent || hasSubmissionId
        ? await patchMethod({
            url: `${ADVERTISER_SUBMIT_JOB[1]}${
              formData?.submissionId || submissionId || editJobSubmissionId
            }/`,
            ...requestConfig
          })
        : await postMethod({
            url: ADVERTISER_SUBMIT_JOB[0],
            params: {
              location: location?.id,
              login_done: isAndroidWebview ? 1 : loginDetails?.isLogIn ? 1 : 0,
              locale: languageFromAndroid,
              en: 0
            },
            ...requestConfig
          })

    /* ---------- 3.  Error / success handling ---------- */
    const failed = isTempFormDataPresent
      ? res?.detail === 'Not found.'
      : !statusCodes.includes(res?.status)

    if (failed) {
      if (postJobPresentStep === 3) {
        setSubmissionError(res?.error?.data)
        if (res?.error?.data?.field === 'company_email') {
          localStorage.setItem('submissionError', res?.error?.data?.message)
          setSubmissionError(res?.error?.data?.message)
        }
      }
      trackEvent({
        name: 'Job_Posted_unsuccessfully',
        searchParams: utmParams,
        options: advertiserAnalyticOptions({
          'Mobile number': loginDetails?.mobileNumber,
          'WhatsApp Opt_in': loginDetails?.whatsAppOptIn,
          'Job district': formData?.location,
          'Job constituency': formData?.constituency,
          enterprise: !!enterpriseLoginDetails?.advertiserId,
          status_code: `Status code ${res?.status}`,
          response: res?.detail || JSON.stringify(res)
        }),
        isJobPostedSuccessfully: false
      })
      return { error: true }
    }

    /* ---------- success branch ---------- */
    if (!isTempFormDataPresent) {
      trackEvent({
        name: 'Job_Posted_successfully',
        searchParams: utmParams,
        options: advertiserAnalyticOptions({
          'Mobile number': loginDetails?.mobileNumber,
          'WhatsApp Opt_in': loginDetails?.whatsAppOptIn,
          'Job district': formData?.location,
          'Job constituency': formData?.constituency,
          submission_id: res.data['submission_id'],
          enterprise: !!enterpriseLoginDetails?.advertiserId
        }),
        isJobPostedSuccessfully: true
      })
    } else {
      trackEvent({
        name: 'tap_edit_jobsubmission',
        searchParams: utmParams,
        options: advertiserAnalyticOptions({
          post_id: formData?.classified,
          enterprise: !!enterpriseLoginDetails?.advertiserId
        }),
        isFbPixelThere: false
      })
    }
    setFormData((prev) => ({ ...prev, submissionId: res?.data?.submission_id }))
    setQueryParams({
      submission_id: res?.data?.submission_id || formData?.['submissionId'] || res?.data?.id
    })
    if (isLegacyNewsWebView() && res?.data?.submission_id) {
      Android.submitForm(
        JSON.stringify({
          ...result,
          job_location_title: formData?.['job_location_title'],
          job_city_title: formData?.['job_city_title'],
          id: res?.data?.submission_id
        })
      )
    }

    return {
      error: false,
      redirectionState: res?.data?.redirection_state ?? 1 // 1 → dashboard, 2 → payment
    }
  }, [
    utmParams,
    loginDetails,
    formData,
    tempFormData,
    submissionId,
    editJobSubmissionId,
    enterpriseLoginDetails
  ])

  const stepsHandler = useCallback(
    async (button, enterpriseAdvertiserId) => {
      // prevent concurrent clicks while an async run is active
      if (btnDisable) return true

      const { ids } = findErrorIds(button, enterpriseAdvertiserId)

      /* --- 1. validation failure --- */
      if (ids?.length) {
        trackEvent({
          name: getEventName({ presentStep: postJobPresentStep, key: 'error' }),
          searchParams: utmParams,
          options: advertiserAnalyticOptions({
            'Mobile number': loginDetails?.mobileNumber,
            'WhatsApp Opt_in': loginDetails?.whatsAppOptIn,
            'Job district': formData?.districts,
            'Job constituency': formData?.constituency,
            'missing fields': ids,
            enterprise: !!enterpriseLoginDetails?.advertiserId
          })
        })
        setErrorId((prev) => [...ids, ...prev])
        scrollToElement(ids?.[0])
        return true // stay on current step
      }

      /* --- 2. show loader & draft save --- */
      trackEvent({
        name: getEventName({ presentStep: postJobPresentStep, key: 'success' }),
        searchParams: utmParams,
        options: advertiserAnalyticOptions({
          Page_no: `page_${postJobPresentStep}`,
          Event_type: postJobPresentStep == 3 ? 'conversion' : 'tap',
          enterprise: !!enterpriseLoginDetails?.advertiserId
        })
      })
      setbtnLoader(true)
      setbtnDisable(true)
      try {
        await draftPost({
          step: postJobPresentStep,
          formData,
          isAndroidWebview: isAndroid(),
          locale: languageFromAndroid,
          isEnterprise: !!enterpriseLoginDetails?.advertiserId
        })

        let proceedToNextStep = true

        /* --- 3. step-3 special: submission / redirection_state --- */
        if (postJobPresentStep === 3) {
          const { error, redirectionState } = await handleSubmit()

          if (error) {
            setShowErrorModal(true)
            proceedToNextStep = false
          } else {
            // redirection_state: 1 → finish flow, 2 → payment step
            proceedToNextStep = redirectionState === 2
          }
        }

        /* --- 4. move or finish --- */
        if (proceedToNextStep) {
          setShowErrorModal(false)
          setpostJobPresentStep((prev) => (prev < NUMBER_OF_STEPS.current ? prev + 1 : prev))
          return true // internal navigation only
        }

        // flow finished → reset & redirect
        setFormData({})
        setFormContext({})
        setTempFormData({})
        setShowErrorModal(false)

        if (isLegacyNewsWebView()) {
          Android.onFinishFlow()
        } else {
          replaceThePageInAdvertiser({
            pathName: 'advertiserDashboardPage',
            as: 'dashboard'
          })
        }
        return false // component will unmount
      } finally {
        setbtnLoader(false)
        setbtnDisable(false)
      }
    },
    [postJobPresentStep, formData, loginDetails, utmParams, enterpriseLoginDetails, btnDisable]
  )

  const backHandler = () => {
    if (isLegacyNewsWebView()) {
      if (postJobPresentStep === 1) Android.closeWebForm()
      else if (postJobPresentStep === 4) Android.onFinishFlow()
      else {
        setpostJobPresentStep((prv) => (prv != 1 ? prv - 1 : prv))
      }
      return
    }
    if (postJobPresentStep === 1) {
      replaceThePageInAdvertiser({
        pathName: 'advertiserDashboardPage',
        as: 'dashboard'
      })
    } else {
      setpostJobPresentStep((prv) => (prv != 1 ? prv - 1 : prv))
    }
  }

  // To be edited for android webview
  function web_android_back_nav() {
    if (postJobPresentStep === 1) Android.closeWebForm()
    else if (postJobPresentStep === 4) Android.onFinishFlow()
    else {
      setpostJobPresentStep((prv) => (prv != 1 ? prv - 1 : prv))
    }
  }

  // Attach the function to the global scope (window)

  if (isLegacyNewsWebView()) {
    window.web_android_back_nav = web_android_back_nav
  }

  const stepsComponents = useMemo(
    () => [
      <Step1
        {...{
          formData,
          onChange,
          context,
          conditions,
          submissionId
        }}
      />,
      <Step2
        {...{
          formData,
          onChange,
          context,
          conditions
        }}
      />,
      <Step3
        {...{
          formData,
          onChange,
          context
        }}
      />
    ],
    [formData, data?.context]
  )

  return (
    <PostJobContext.Provider value={{ errorId, isPostJobMounted, setErrorId, submissionError }}>
      {loader && (
        <div className="flex h-full items-center justify-center backdrop:brightness-0">
          <CircularSpinner />
        </div>
      )}
      {showErrorModal && (
        <FooterModal
          {...{
            isOpen: showErrorModal,
            handleModalClose: () => {
              setShowErrorModal(false)
            },
            isHeaderPresent: true,
            headerClassName: '',
            className: 'max-h-[85vh]'
          }}
        >
          <div className="px-16">
            <EnterpriseErrorComponent
              {...{
                IconComponent: !isOnline
                  ? SignalWifiConnectedNoInternet4Icon
                  : ErrorOutlineRoundedIcon,
                title: !isOnline ? 'No internet connection' : 'Something went wrong!',
                content: !isOnline ? 'Please check your connection' : 'Please try again',
                isButton: true,
                buttonTitle: 'Try again',
                buttonClickHandler: () => {
                  return stepsHandler('next', enterpriseLoginDetails?.advertiserId)
                },
                iconClassName: 'h-16 w-16 text-redAlert900',
                titleClassName: 'leading-30 text-xxl mt-lg',
                contentClassName: 'text-md mt-sm mb-xl',
                buttonClassName: 'mt-lg mb-lg w-full border-none bg-secondary'
              }}
            />
          </div>
        </FooterModal>
      )}
      {!loader && (
        <>
          {postJobPresentStep == 4 ? (
            <PaymentComponent
              {...{
                formData,
                onChange,
                context,
                conditions,
                totalAmount,
                setTotalAmount,
                submissionId,
                locale
              }}
              ref={childRef}
            />
          ) : (
            <div className="overflow-clip pb-[72px]  transition-all duration-300 backdrop:brightness-0 md:h-full md:pb-[0px]">
              {/* Need to revisit for back navigation UI */}
              <div className="sticky top-0 z-20 bg-[white] shadow-default">
                <Media at="md">
                  <PostJobHeader
                    {...{ postJobPresentStep, id: 'postJobHeader' }}
                    isDashboardButton
                  />
                </Media>

                <Media at="sm">
                  <PostJobHeader
                    {...{ postJobPresentStep, id: 'postJobHeader' }}
                    headerCallback={() => {
                      setIsDistrictSelected(false)
                    }}
                    isDashboardButton={!isLegacyNewsWebView()}
                  />
                </Media>

                <Media greaterThanOrEqual="lg">
                  <AdvertiserHeader isPostButtonPresent={false} isDashboardButton />
                </Media>
              </div>

              <div className=" h-full md:flex md:flex-col md:items-center md:bg-[#F2F2F2] md:py-[24px]">
                <div className="flex h-full flex-1 flex-col bg-white md:rounded-xs md:shadow-default">
                  {/* {Parent containing postJobSteps and AdvertiserForm} */}
                  <div className="removeScroll  overflow-x-scroll">
                    <div
                      className={classNames(
                        ' z-20 bg-white md:relative md:top-[0px] md:z-10 md:w-[900px] md:rounded-xs md:px-[24px] '
                      )}
                      id="post_job_steps"
                    >
                      <PostJobSteps {...{ presentStep: postJobPresentStep }} isCPMFlow={true} />
                    </div>

                    <div className="relative flex h-full flex-1 flex-col transition-all duration-300 sm:py-24 md:py-[0px] md:pb-[24px]">
                      {stepsComponents?.[postJobPresentStep - 1]}
                    </div>
                  </div>
                  {/* Bottom Banner of the Job Posting Form */}
                  <div className=" mb-[16px] sm:mb-[0px] md:px-[24px]">
                    <div
                      className={classNames(
                        'fixed bottom-0 z-20 h-[72px] w-full border-[0px] border-solid border-[#CBCCCE] bg-white px-[16px] py-[12px] md:relative md:h-fit md:place-content-end md:border-t-xs md:px-[0px] md:pb-[24px] md:pt-[8px]',
                        postJobPresentStep == 4
                          ? 'h-[122px] sm:pt-[0px] md:pt-[8px]'
                          : 'h-[72px] md:pt-[24px]'
                      )}
                    >
                      {postJobPresentStep == 4 ? (
                        <div
                          className={classNames(
                            'flex ',
                            windowSize > 640 ? 'pt-[16px] pb-[24px]' : 'py-[16px]'
                          )}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={20}
                            height={20}
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <mask
                              id="mask0_2976_19310"
                              style={{
                                maskType: 'alpha'
                              }}
                              maskUnits="userSpaceOnUse"
                              x={0}
                              y={0}
                              width={20}
                              height={20}
                            >
                              <rect width={20} height={20} fill="#D9D9D9" />
                            </mask>
                            <g mask="url(#mask0_2976_19310)">
                              <path
                                d="M16.625 17.5C14.8333 17.5 13.0868 17.1007 11.3854 16.3021C9.68403 15.5035 8.17708 14.4479 6.86458 13.1354C5.55208 11.8229 4.49653 10.316 3.69792 8.61458C2.89931 6.91319 2.5 5.16667 2.5 3.375C2.5 3.125 2.58333 2.91667 2.75 2.75C2.91667 2.58333 3.125 2.5 3.375 2.5H6.75C6.94444 2.5 7.11806 2.5625 7.27083 2.6875C7.42361 2.8125 7.51389 2.97222 7.54167 3.16667L8.08333 6.08333C8.11111 6.27778 8.10764 6.45486 8.07292 6.61458C8.03819 6.77431 7.95833 6.91667 7.83333 7.04167L5.8125 9.08333C6.39583 10.0833 7.12847 11.0208 8.01042 11.8958C8.89236 12.7708 9.86111 13.5278 10.9167 14.1667L12.875 12.2083C13 12.0833 13.1632 11.9896 13.3646 11.9271C13.566 11.8646 13.7639 11.8472 13.9583 11.875L16.8333 12.4583C17.0278 12.5 17.1875 12.5938 17.3125 12.7396C17.4375 12.8854 17.5 13.0556 17.5 13.25V16.625C17.5 16.875 17.4167 17.0833 17.25 17.25C17.0833 17.4167 16.875 17.5 16.625 17.5ZM5.02083 7.5L6.39583 6.125L6.04167 4.16667H4.1875C4.25694 4.73611 4.35417 5.29861 4.47917 5.85417C4.60417 6.40972 4.78472 6.95833 5.02083 7.5ZM15.8333 15.7917V13.9583L13.875 13.5625L12.4792 14.9583C13.0208 15.1944 13.5729 15.3819 14.1354 15.5208C14.6979 15.6597 15.2639 15.75 15.8333 15.7917Z"
                                fill="#332500"
                              />
                            </g>
                          </svg>
                          <div
                            className={classNames(
                              'pl-[8px]',
                              windowSize > 640
                                ? '  font-noto-sans text-[14px] font-normal leading-[21px] text-[#323232]'
                                : 'font-noto-sans text-[12px] font-normal leading-[16px] text-[#323232]'
                            )}
                          >
                            For any queries, reach us at
                            <span className="ml-[4px] font-[700]">
                              {' '}
                              +91{' '}
                              {getStep4PhoneNumberFromLocale(
                                isAndroidWebview ? languageFromAndroid : locale
                              )}
                            </span>
                          </div>
                        </div>
                      ) : null}
                      {/* { Buttons related flow } */}
                      <div className={classNames('flex md:justify-end')}>
                        <div className="w-full">
                          {postJobPresentStep != 4 && (
                            <div className="flex justify-between">
                              <AdvertiserPrimaryButton
                                title={translation?.postJobSteps?.back}
                                isArrowPresent={false}
                                containerClassName={classNames(
                                  'w-full max-w-[152px] relative rounded-[3px]'
                                )}
                                extraClassName={
                                  'bg-[#ffffff] rounded-sm border-solid border-[1px] border-[#989898] hover:border-[1px]'
                                }
                                onClick={backHandler}
                              />

                              <NextLink
                                id="next"
                                href={{ pathname: '/advertiserDashboardPage' }}
                                as="/hire/dashboard"
                                isLoader={false}
                                disabled={btnLoader || btnDisable}
                                onClick={() =>
                                  stepsHandler('next', enterpriseLoginDetails?.advertiserId)
                                }
                              >
                                <AdvertiserPrimaryButton
                                  title={
                                    postJobPresentStep === NUMBER_OF_STEPS?.current
                                      ? 'Proceed'
                                      : translation?.postJobSteps?.next
                                  }
                                  isArrowPresent={true}
                                  containerClassName={classNames(
                                    'w-full min-w-[152px] relative rounded-[3px]'
                                  )}
                                  extraClassName={'bg-secondary'}
                                  loading={btnLoader}
                                  disabled={btnDisable}
                                />
                              </NextLink>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </PostJobContext.Provider>
  )
}
